import { Slot } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow] disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        /** Ação principal da tela/contexto — uma por vez. */
        default:
          'bg-primary text-primary-foreground hover:bg-[color-mix(in_srgb,var(--primary)_88%,black)] focus-visible:ring-primary/35',
        destructive:
          'bg-destructive text-white hover:bg-[color-mix(in_srgb,var(--destructive)_88%,black)] focus-visible:ring-destructive/30',
        /** Ação secundária: borda neutra sobre superfície. */
        outline:
          'border border-border-strong bg-card text-foreground hover:bg-muted hover:text-foreground',
        secondary: 'bg-muted text-foreground hover:bg-accent',
        /** Destaque suave no verde (filtros ativos, ações contextuais). */
        soft: 'bg-primary-soft text-primary hover:bg-[color-mix(in_srgb,var(--primary)_14%,white)] dark:hover:bg-primary/20',
        ghost: 'text-foreground hover:bg-muted',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-3.5 has-[>svg]:px-3',
        sm: 'h-8 gap-1.5 px-3 text-[13px] has-[>svg]:px-2.5',
        lg: 'h-11 rounded-lg px-5 text-[15px] has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-11 rounded-lg',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Desabilita e troca o ícone inicial por um indicador de carregamento. */
    loading?: boolean;
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  if (asChild) {
    return (
      <Slot.Root
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {children}
      </Slot.Root>
    );
  }
  // Com loading, o primeiro ícone (se houver) é substituído pelo spinner para manter a largura.
  const content = loading
    ? React.Children.toArray(children).reduce<{ out: React.ReactNode[]; replaced: boolean }>(
        (acc, child) => {
          if (!acc.replaced && React.isValidElement(child) && typeof child.type !== 'string') {
            acc.out.push(<Loader2 key="loading" className="animate-spin" aria-hidden />);
            acc.replaced = true;
          } else acc.out.push(child);
          return acc;
        },
        { out: [], replaced: false },
      )
    : null;
  return (
    <button
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {content
        ? content.replaced
          ? content.out
          : [<Loader2 key="loading" className="animate-spin" aria-hidden />, ...content.out]
        : children}
    </button>
  );
}

export { Button, buttonVariants };
